import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calculator, Utensils, Save, RefreshCw, Plus, FileText, Trash2, AlertCircle, Info, CheckCircle, Scale } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { FOOD_DATABASE } from '../data/foodDatabase';
import GramEditor from '../components/GramEditor';
import {
  calculateProteinPortion,
  calculateSmartFat,
  calculatePortionForSelect,
  filterFoodsByPreferences,
  filterFoodsByDietType,
  roundGramsForDisplay,
  getFoodById
} from '../utils/nutritionCalculator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MAX_MENUS = 6;

// Distribuciones de macros por tipo de dieta
const DIET_DISTRIBUTIONS = {
  equilibrada: { proteinas: 0.30, carbohidratos: 0.40, grasas: 0.30 },
  alta_proteina: { proteinas: 0.40, carbohidratos: 0.30, grasas: 0.30 },
  alta_carbos: { proteinas: 0.25, carbohidratos: 0.50, grasas: 0.25 },
  baja_carbos: { proteinas: 0.35, carbohidratos: 0.25, grasas: 0.40 },
  cetogenica: { proteinas: 0.25, carbohidratos: 0.05, grasas: 0.70 },
  alta_grasas: { proteinas: 0.25, carbohidratos: 0.30, grasas: 0.45 }
};

// Explicaciones de cada tipo de dieta
const DIET_EXPLANATIONS = {
  equilibrada: "Recomendada si no presentas problemas digestivos con ningún macronutriente y buscas una alimentación balanceada. Es ideal para la mayoría de las personas que desean mantener energía estable durante el día y una dieta sostenible a largo plazo.",
  alta_proteina: "Ideal si tu objetivo es aumentar o mantener masa muscular, mejorar la saciedad o controlar el apetito. También puede ser útil si entrenas con frecuencia o estás en un proceso de pérdida de grasa.",
  alta_carbos: "Recomendada si tienes un metabolismo rápido, realizas entrenamientos intensos o frecuentes, o si sueles perder peso con facilidad. Este tipo de distribución puede ayudarte a mantener niveles altos de energía y mejorar el rendimiento físico.",
  baja_carbos: "Puede ser una buena opción si tiendes a sentirte inflamado al consumir muchos carbohidratos, si te cuesta perder grasa corporal o si prefieres una alimentación con mayor cantidad de grasas y proteínas que te mantenga saciado por más tiempo.",
  cetogenica: "Indicado para quienes desean seguir una dieta muy baja en carbohidratos y alta en grasas. Este enfoque busca que el cuerpo utilice principalmente grasa como fuente de energía. Suele ser más restrictivo y se recomienda para personas que ya están familiarizadas con este tipo de alimentación.",
  alta_grasas: "Recomendada para personas que prefieren una alimentación con mayor presencia de grasas saludables como aguacate, frutos secos, aceite de oliva y pescados grasos, manteniendo una cantidad moderada de carbohidratos sin llegar a una dieta cetogénica."
};

const DIET_LABELS = {
  equilibrada: { emoji: '⚖️', label: 'Equilibrada' },
  alta_proteina: { emoji: '🥩', label: 'Alta en Proteína' },
  alta_carbos: { emoji: '🍚', label: 'Alta en Carbohidratos' },
  baja_carbos: { emoji: '🥑', label: 'Baja en Carbos (Low Carb)' },
  cetogenica: { emoji: '🥓', label: 'Cetogénica (Keto)' },
  alta_grasas: { emoji: '🫒', label: 'Alta en Grasas Saludables' }
};

const NutritionFormPremium = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [existingForm, setExistingForm] = useState(null);
  const [nutritionPlan, setNutritionPlan] = useState(null);
  const [showPlanBuilder, setShowPlanBuilder] = useState(false);
  const [selectedFoods, setSelectedFoods] = useState({});
  // Gramos editables por comida y macro. Key: `meal_${i}_${cat}`  -> number
  const [mealGrams, setMealGrams] = useState({});
  // Comidas donde el usuario ha ocultado los vegetales (Set de índices)
  const [hiddenVegetables, setHiddenVegetables] = useState(new Set());
  const [savedMenus, setSavedMenus] = useState([]);
  const [menusCreatedThisMonth, setMenusCreatedThisMonth] = useState(0);
  const [selectedMenuIndex, setSelectedMenuIndex] = useState(null);
  const [menuType, setMenuType] = useState('grasa');
  const [userObjective, setUserObjective] = useState('perder');
  const [displayGoal, setDisplayGoal] = useState('grasa');
  const [showDietExplanation, setShowDietExplanation] = useState(false);

  const [formData, setFormData] = useState({
    sexo: 'mujer',
    edad: '',
    peso: '',
    estatura: '',
    nivel_actividad: 'moderado',
    objetivo: 'mantener',
    // Perfil metabólico
    reaccion_carbos: 'normal',
    nivel_hambre: 'moderado',
    tipo_entrenamiento: 'mixto',
    horario_entreno: 'tarde',
    velocidad_metabolismo: 'normal',
    retencion_liquidos: 'no',
    calidad_sueno: 'buena',
    // Preferencia de dieta
    preferencia_dieta: 'equilibrada',
    // Comida post entreno
    comida_post_entreno: '2',
    // Otros
    alergias: '',
    alimentos_no_gustan: '',
    numero_comidas: '5',
    tipo_alimentacion: 'ninguna'
  });

  useEffect(() => {
    checkAuth();
  }, []);

  // Auto-seleccionar la dieta recomendada cuando cambian las respuestas del perfil metabólico
  useEffect(() => {
    const recommended = getRecommendedDiet();
    if (recommended && recommended !== formData.preferencia_dieta) {
      setFormData(prev => ({ ...prev, preferencia_dieta: recommended }));
    }
  }, [formData.reaccion_carbos, formData.nivel_hambre, formData.tipo_entrenamiento, formData.velocidad_metabolismo, formData.retencion_liquidos, formData.objetivo]);

  const checkAuth = async () => {
    const token = localStorage.getItem('session_token');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const userRes = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(userRes.data);

      // Obtener menús guardados del usuario
      const menusRes = await axios.get(`${BACKEND_URL}/api/nutrition-form/my-menus`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (menusRes.data) {
        setSavedMenus(menusRes.data.menus || []);
        setMenusCreatedThisMonth(menusRes.data.menus_this_month || 0);
        if (menusRes.data.nutrition_plan) {
          setNutritionPlan(menusRes.data.nutrition_plan);
          setShowPlanBuilder(true);
        }
        if (menusRes.data.form_data) {
          setExistingForm(menusRes.data.form_data);
          const savedObjective = menusRes.data.form_data.objetivo;
          if (savedObjective) {
            setUserObjective(savedObjective);
            if (savedObjective === 'ganar') {
              setDisplayGoal('masa');
            } else {
              setDisplayGoal('grasa');
            }
          }
          if (menusRes.data.form_data.tipo_alimentacion) {
            setFormData(prev => ({ ...prev, tipo_alimentacion: menusRes.data.form_data.tipo_alimentacion }));
          }
        }
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Error:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  // Mifflin-St Jeor (más preciso que Harris-Benedict)
  const calculateMifflinStJeor = (peso, estatura, edad, sexo) => {
    if (sexo === 'hombre') {
      return (10 * peso) + (6.25 * estatura) - (5 * edad) + 5;
    } else {
      return (10 * peso) + (6.25 * estatura) - (5 * edad) - 161;
    }
  };

  const getActivityFactor = (nivel) => {
    const factors = {
      sedentario: 1.2,
      ligero: 1.375,
      moderado: 1.55,
      alto: 1.725
    };
    return factors[nivel] || 1.55;
  };

  const getObjectiveAdjustment = (objetivo, tdee) => {
    const adjustments = {
      perder: -0.20,        // -20% déficit
      ganar: 0.15,          // +15% superávit
      mantener: 0,
      recomposicion: -0.10  // -10% ligero déficit
    };
    return Math.round(tdee * (adjustments[objetivo] || 0));
  };

  // Determinar la dieta recomendada según el perfil metabólico completo
  const getRecommendedDiet = () => {
    const { reaccion_carbos, nivel_hambre, objetivo, tipo_entrenamiento, velocidad_metabolismo, retencion_liquidos } = formData;
    
    // Sistema de puntuación por tipo de dieta
    const scores = {
      equilibrada: 0,
      alta_proteina: 0,
      alta_carbos: 0,
      baja_carbos: 0,
      cetogenica: 0,
      alta_grasas: 0
    };
    
    // Reacción a carbohidratos
    if (reaccion_carbos === 'inflamado') {
      scores.baja_carbos += 3;
      scores.alta_grasas += 2;
      scores.cetogenica += 1;
    } else if (reaccion_carbos === 'energia') {
      scores.alta_carbos += 3;
      scores.equilibrada += 1;
    } else {
      scores.equilibrada += 2;
    }
    
    // Nivel de hambre
    if (nivel_hambre === 'alto') {
      scores.alta_proteina += 3;
      scores.alta_grasas += 1;
    } else if (nivel_hambre === 'bajo') {
      scores.equilibrada += 1;
    }
    
    // Objetivo
    if (objetivo === 'perder') {
      scores.alta_proteina += 2;
      scores.baja_carbos += 1;
    } else if (objetivo === 'ganar') {
      scores.alta_carbos += 2;
      scores.alta_proteina += 1;
    } else if (objetivo === 'recomposicion') {
      scores.alta_proteina += 2;
    }
    
    // Tipo de entrenamiento
    if (tipo_entrenamiento === 'pesas') {
      scores.alta_proteina += 2;
    } else if (tipo_entrenamiento === 'cardio') {
      scores.alta_carbos += 2;
    } else if (tipo_entrenamiento === 'funcional') {
      scores.equilibrada += 2;
    }
    
    // Velocidad de metabolismo
    if (velocidad_metabolismo === 'rapido') {
      scores.alta_carbos += 2;
      scores.alta_proteina += 1;
    } else if (velocidad_metabolismo === 'lento') {
      scores.baja_carbos += 2;
      scores.alta_grasas += 1;
    }
    
    // Retención de líquidos
    if (retencion_liquidos === 'si') {
      scores.baja_carbos += 1;
    }
    
    // Encontrar la dieta con mayor puntuación
    let best = 'equilibrada';
    let maxScore = scores.equilibrada;
    for (const [diet, score] of Object.entries(scores)) {
      if (score > maxScore) {
        maxScore = score;
        best = diet;
      }
    }
    
    return best;
  };

  // Generar diagnóstico personalizado basado en las respuestas
  const getDiagnostic = () => {
    const { reaccion_carbos, nivel_hambre, tipo_entrenamiento, velocidad_metabolismo, retencion_liquidos, calidad_sueno, objetivo } = formData;
    const recommended = getRecommendedDiet();
    
    // Tipo metabólico
    let metabolicType = '';
    let metabolicDescription = '';
    
    if (velocidad_metabolismo === 'rapido' && reaccion_carbos === 'energia') {
      metabolicType = 'Metabolismo Acelerado';
      metabolicDescription = 'Tu cuerpo procesa los carbohidratos eficientemente y quemas calorías rápidamente. Necesitas un aporte calórico alto para mantener tu masa muscular.';
    } else if (velocidad_metabolismo === 'lento' && reaccion_carbos === 'inflamado') {
      metabolicType = 'Metabolismo Conservador';
      metabolicDescription = 'Tu cuerpo tiende a almacenar energía y es sensible a los carbohidratos. Una dieta con menos carbos y más proteína/grasas saludables te ayudará a sentirte mejor.';
    } else if (reaccion_carbos === 'inflamado') {
      metabolicType = 'Sensibilidad a Carbohidratos';
      metabolicDescription = 'Tu cuerpo reacciona negativamente a excesos de carbohidratos. Reducirlos y priorizar proteínas y grasas saludables mejorará tu digestión y energía.';
    } else if (nivel_hambre === 'alto' && objetivo === 'perder') {
      metabolicType = 'Metabolismo Demandante';
      metabolicDescription = 'Tu nivel de hambre elevado sugiere que necesitas comidas más saciantes. Priorizar proteína te ayudará a controlar el apetito mientras pierdes grasa.';
    } else if (velocidad_metabolismo === 'rapido') {
      metabolicType = 'Metabolismo Rápido';
      metabolicDescription = 'Quemas calorías con facilidad. Necesitas un buen aporte calórico, especialmente de carbohidratos, para mantener energía y rendimiento.';
    } else {
      metabolicType = 'Metabolismo Equilibrado';
      metabolicDescription = 'Tu perfil metabólico es estable. Una distribución equilibrada de macronutrientes con ajustes según tu objetivo será lo más efectivo.';
    }
    
    // Consejos personalizados
    const tips = [];
    
    if (calidad_sueno === 'mala') {
      tips.push('Tu calidad de sueño afecta directamente tu recuperación muscular y niveles de cortisol. Prioriza dormir 7-8 horas para mejores resultados.');
    }
    if (retencion_liquidos === 'si') {
      tips.push('Para reducir la retención de líquidos, aumenta tu consumo de agua a 2-3 litros diarios y reduce el sodio.');
    }
    if (tipo_entrenamiento === 'pesas' && objetivo === 'ganar') {
      tips.push('Con entrenamiento de pesas y objetivo de ganar masa, la proteína es tu mejor aliada. Asegúrate de consumirla en cada comida.');
    }
    if (tipo_entrenamiento === 'cardio' && objetivo === 'perder') {
      tips.push('El cardio combinado con déficit calórico es efectivo, pero no descuides la proteína para preservar tu masa muscular.');
    }
    
    return { metabolicType, metabolicDescription, recommended, tips };
  };

  const filterFoods = (foods) => {
    const byPreferences = filterFoodsByPreferences(foods, formData.alergias, formData.alimentos_no_gustan);
    return filterFoodsByDietType(byPreferences, formData.tipo_alimentacion);
  };

  const handleFoodSelect = (mealIndex, category, foodId) => {
    setSelectedFoods(prev => ({
      ...prev,
      [`meal_${mealIndex}_${category}`]: foodId
    }));

    // Al elegir un alimento, pre-calcular los gramos por defecto para el editor
    if (!foodId) {
      setMealGrams(prev => ({ ...prev, [`meal_${mealIndex}_${category}`]: 0 }));
      return;
    }
    const food = getFoodById(category, foodId);
    if (!food || !nutritionPlan) return;

    const numMeals = nutritionPlan.comidas_por_dia;
    const macros = nutritionPlan.macros;
    if (!macros) return;
    const protPerMeal = Math.round(macros.proteinas_g / numMeals);
    const carbPerMeal = Math.round(macros.carbohidratos_g / numMeals);
    const fatPerMeal = Math.round(macros.grasas_g / numMeals);

    let grams = 0;
    if (category === 'proteina' && food.proteinas > 0) {
      grams = Math.round((protPerMeal / food.proteinas) * 100);
    } else if (category === 'carbohidrato' && food.carbohidratos > 0) {
      grams = Math.round((carbPerMeal / food.carbohidratos) * 100);
    } else if (category === 'grasa' && food.grasas > 0) {
      grams = Math.round((fatPerMeal / food.grasas) * 100);
    } else if (category === 'fruta' && food.carbohidratos > 0) {
      grams = Math.round((15 / food.carbohidratos) * 100);
    }

    setMealGrams(prev => ({ ...prev, [`meal_${mealIndex}_${category}`]: grams }));
  };

  const setMealFoodGramsPremium = (mealIndex, category, newGrams) => {
    const v = Math.max(parseInt(newGrams) || 0, 0);
    setMealGrams(prev => ({ ...prev, [`meal_${mealIndex}_${category}`]: v }));
  };

  const adjustMealFoodGramsPremium = (mealIndex, category, delta) => {
    setMealGrams(prev => {
      const key = `meal_${mealIndex}_${category}`;
      const cur = prev[key] || 0;
      return { ...prev, [key]: Math.max(cur + delta, 0) };
    });
  };

  /**
   * Calcula los totales del día en Premium a partir de selectedFoods + mealGrams.
   */
  const calculateDailyTotalsPremium = () => {
    if (!nutritionPlan) return { cal: 0, p: 0, c: 0, g: 0 };
    const totals = { cal: 0, p: 0, c: 0, g: 0 };
    const numMeals = nutritionPlan.comidas_por_dia;
    for (let i = 0; i < numMeals; i++) {
      ['proteina', 'carbohidrato', 'grasa', 'fruta'].forEach(cat => {
        const foodId = selectedFoods[`meal_${i}_${cat}`];
        const grams = mealGrams[`meal_${i}_${cat}`] || 0;
        if (foodId && grams > 0) {
          const food = getFoodById(cat, foodId);
          if (food) {
            const mult = grams / 100;
            totals.cal += food.calorias * mult;
            totals.p += food.proteinas * mult;
            totals.c += food.carbohidratos * mult;
            totals.g += food.grasas * mult;
          }
        }
      });
    }
    return {
      cal: Math.round(totals.cal),
      p: Math.round(totals.p),
      c: Math.round(totals.c),
      g: Math.round(totals.g)
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.edad || !formData.estatura || !formData.peso) {
      toast.error('Por favor completa todos los campos obligatorios');
      return;
    }

    // Validar que estatura no tenga punto decimal
    if (formData.estatura.includes('.') || formData.estatura.includes(',')) {
      toast.error('La estatura debe estar en centímetros sin puntos. Ejemplo: 162');
      return;
    }

    setSubmitting(true);
    const token = localStorage.getItem('session_token');

    try {
      // Calcular TMB con Mifflin-St Jeor
      const tmb = calculateMifflinStJeor(
        parseFloat(formData.peso),
        parseFloat(formData.estatura),
        parseInt(formData.edad),
        formData.sexo
      );
      
      const activityFactor = getActivityFactor(formData.nivel_actividad);
      const tdee = tmb * activityFactor;
      const objectiveAdjustment = getObjectiveAdjustment(formData.objetivo, tdee);
      const caloriasDiarias = Math.round(tdee + objectiveAdjustment);

      // Obtener distribución de macros según preferencia de dieta
      const distribution = DIET_DISTRIBUTIONS[formData.preferencia_dieta];
      
      // Calcular macros: proteína y carbos = 4 cal/g, grasa = 9 cal/g
      const proteinas = Math.round((caloriasDiarias * distribution.proteinas) / 4);
      const carbohidratos = Math.round((caloriasDiarias * distribution.carbohidratos) / 4);
      const grasas = Math.round((caloriasDiarias * distribution.grasas) / 9);

      const numComidas = parseInt(formData.numero_comidas);

      const nutritionPlanData = {
        tmb: Math.round(tmb),
        tdee: Math.round(tdee),
        calorias_diarias: caloriasDiarias,
        macros: {
          proteinas_g: proteinas,
          carbohidratos_g: carbohidratos,
          grasas_g: grasas
        },
        distribucion: {
          proteinas_pct: Math.round(distribution.proteinas * 100),
          carbohidratos_pct: Math.round(distribution.carbohidratos * 100),
          grasas_pct: Math.round(distribution.grasas * 100)
        },
        preferencia_dieta: formData.preferencia_dieta,
        comidas_por_dia: numComidas,
        comida_post_entreno: formData.comida_post_entreno,
        calorias_por_comida: Math.round(caloriasDiarias / numComidas),
        macros_por_comida: {
          proteinas: Math.round(proteinas / numComidas),
          carbohidratos: Math.round(carbohidratos / numComidas),
          grasas: Math.round(grasas / numComidas)
        }
      };

      // Guardar datos del formulario
      await axios.post(`${BACKEND_URL}/api/nutrition-form/submit-level2`, {
        ...formData,
        nombre_completo: user?.name || '',
        correo_electronico: user?.email || '',
        nutrition_plan: nutritionPlanData,
        level: 'premium'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('¡Plan calculado! Según tu perfil metabólico, esta es la distribución más adecuada para ti.');
      setNutritionPlan(nutritionPlanData);
      setUserObjective(formData.objetivo);
      if (formData.objetivo === 'ganar') {
        setDisplayGoal('masa');
      } else {
        setDisplayGoal('grasa');
      }
      setShowPlanBuilder(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al calcular el plan');
    } finally {
      setSubmitting(false);
    }
  };

  // Contar menús por tipo
  const masaMenus = savedMenus.filter(m => m.menu_type === 'masa').length;
  const grasaMenus = savedMenus.filter(m => m.menu_type === 'grasa').length;

  const generateMenu = async () => {
    const selectedType = displayGoal;
    
    if (savedMenus.length >= MAX_MENUS) {
      toast.error('Tienes 6 menús guardados. Elimina uno para crear otro.');
      return;
    }

    if (Object.keys(selectedFoods).length === 0) {
      toast.error('Selecciona al menos un alimento para cada comida');
      return;
    }

    const token = localStorage.getItem('session_token');
    
    try {
      // Calcular las calorías ajustadas según el tipo de menú
      const currentDiet = nutritionPlan.preferencia_dieta || 'equilibrada';
      const distribution = DIET_DISTRIBUTIONS[currentDiet];
      
      let menuCalories = nutritionPlan.tdee || nutritionPlan.calorias_diarias;
      if (selectedType === 'grasa') {
        menuCalories = Math.round(menuCalories * 0.80);
      } else if (selectedType === 'masa') {
        menuCalories = Math.round(menuCalories * 1.15);
      }
      
      const menuProteins = Math.round((menuCalories * distribution.proteinas) / 4);
      const menuCarbs = Math.round((menuCalories * distribution.carbohidratos) / 4);
      const menuFats = Math.round((menuCalories * distribution.grasas) / 9);
      
      const menuNumber = (selectedType === 'masa' ? masaMenus : grasaMenus) + 1;
      const menuName = selectedType === 'masa' 
        ? `Días Altos ${menuNumber}` 
        : `Días Bajos ${menuNumber}`;
      
      const newMenu = {
        name: menuName,
        menu_type: selectedType,
        foods: selectedFoods,
        meal_grams: mealGrams,
        hidden_vegetables: Array.from(hiddenVegetables),
        nutrition_plan: nutritionPlan,
        calories: menuCalories,
        macros: {
          proteinas: menuProteins,
          carbohidratos: menuCarbs,
          grasas: menuFats
        },
        created_at: new Date().toISOString()
      };
      
      await axios.post(`${BACKEND_URL}/api/nutrition-form/save-menu`, { menu: newMenu }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`¡${menuName} guardado!`);
      setSavedMenus(prev => [...prev, newMenu]);
      setMenusCreatedThisMonth(prev => prev + 1);
      setSelectedFoods({});
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar el menú');
    }
  };

  const deleteMenu = async (menuIndex) => {
    const token = localStorage.getItem('session_token');
    const menu = savedMenus[menuIndex];
    if (!menu || !menu.id) {
      toast.error('Error: menú no encontrado');
      return;
    }
    
    try {
      await axios.delete(`${BACKEND_URL}/api/nutrition-form/delete-menu/${menu.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Menú eliminado');
      setSavedMenus(prev => prev.filter((_, i) => i !== menuIndex));
      if (selectedMenuIndex === menuIndex) {
        setSelectedMenuIndex(null);
      }
    } catch (error) {
      toast.error('Error al eliminar el menú');
    }
  };

  const getSelectedFoodDetails = (mealIndex, category, foods) => {
    const foodId = foods[`meal_${mealIndex}_${category}`];
    return getFoodById(category, foodId);
  };

  const canCreateMore = savedMenus.length < MAX_MENUS;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#09090b]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
      </div>
    );
  }

  // Vista del Plan Builder (después de calcular)
  if (showPlanBuilder && nutritionPlan) {
    const currentDiet = nutritionPlan.preferencia_dieta || 'equilibrada';
    const distribution = DIET_DISTRIBUTIONS[currentDiet];
    
    // Ajustar calorías según el objetivo seleccionado (Reducir Grasa vs Ganar Masa)
    let adjustedCalories = nutritionPlan.tdee || nutritionPlan.calorias_diarias;
    
    if (displayGoal === 'grasa') {
      // Déficit del 20% para perder grasa
      adjustedCalories = Math.round(adjustedCalories * 0.80);
    } else if (displayGoal === 'masa') {
      // Superávit del 15% para ganar masa
      adjustedCalories = Math.round(adjustedCalories * 1.15);
    }
    
    // Recalcular macros con las calorías ajustadas
    const adjustedProteins = Math.round((adjustedCalories * distribution.proteinas) / 4);
    const adjustedCarbs = Math.round((adjustedCalories * distribution.carbohidratos) / 4);
    const adjustedFats = Math.round((adjustedCalories * distribution.grasas) / 9);
    
    // Calcular por comida
    const numMeals = nutritionPlan.comidas_por_dia;
    const proteinPerMeal = Math.round(adjustedProteins / numMeals);
    const carbsPerMeal = Math.round(adjustedCarbs / numMeals);
    const fatsPerMeal = Math.round(adjustedFats / numMeals);

    // Renderiza el detalle de comidas/alimentos de un menu guardado.
    // Se reutiliza tanto en la vista individual (menu seleccionado) como en el PDF de "todos los menus".
    const renderMenuMeals = (menu) => {
      if (!menu) return null;
      const foods = menu.selected_foods || menu.foods || {};
      const foodEntries = Object.entries(foods);
      const macrosPerMeal = menu.nutrition_plan?.macros_por_comida || {
        proteinas: 34,
        carbohidratos: 45,
        grasas: 15
      };

      if (foodEntries.length === 0) {
        return (
          <p className="text-zinc-500 text-sm text-center py-4">
            Este menu no tiene alimentos guardados
          </p>
        );
      }

      const mealGroups = {};
      foodEntries.forEach(([key, value]) => {
        const match = key.match(/meal_(\d+)_/);
        if (match) {
          const mealIndex = parseInt(match[1]);
          if (!mealGroups[mealIndex]) mealGroups[mealIndex] = {};
          mealGroups[mealIndex][key] = value;
        }
      });

      const calcGrams = (food, targetMacro, macroType) => {
        if (!food) return 0;
        const macroPer100g = food[macroType] || 0;
        if (macroPer100g <= 0) return 0;
        return Math.round((targetMacro / macroPer100g) * 100);
      };

      return Object.entries(mealGroups).map(([mealIndex, mealFoods]) => {
        const proteinId = mealFoods[`meal_${mealIndex}_protein`] || mealFoods[`meal_${mealIndex}_proteina`];
        const carbId = mealFoods[`meal_${mealIndex}_carbs`] || mealFoods[`meal_${mealIndex}_carbohidrato`] || mealFoods[`meal_${mealIndex}_carbo`];
        const fatId = mealFoods[`meal_${mealIndex}_fat`] || mealFoods[`meal_${mealIndex}_grasa`];
        const fruitId = mealFoods[`meal_${mealIndex}_fruit`] || mealFoods[`meal_${mealIndex}_fruta`];

        const protein = getFoodById('proteina', proteinId);
        const carb = getFoodById('carbohidrato', carbId);
        const fat = getFoodById('grasa', fatId);
        const fruit = getFoodById('fruta', fruitId);

        const proteinGrams = calcGrams(protein, macrosPerMeal.proteinas, 'proteinas');
        const carbGrams = calcGrams(carb, macrosPerMeal.carbohidratos, 'carbohidratos');
        const fatGrams = macrosPerMeal.grasas;
        const fruitGrams = fruit ? calcGrams(fruit, 15, 'carbohidratos') : 0;

        return (
          <div key={mealIndex} className="bg-zinc-800/50 rounded-xl p-3">
            <div className="text-sm font-semibold text-zinc-300 mb-2">
              Comida {parseInt(mealIndex) + 1}
            </div>
            <div className="grid grid-cols-1 gap-1.5 text-xs">
              {protein && (
                <div className="flex items-center justify-between text-emerald-400">
                  <span className="flex items-center gap-1">
                    <span>🥩</span>
                    <span>{protein.nombre || protein.name}</span>
                  </span>
                  <span className="text-zinc-400">{proteinGrams}g</span>
                </div>
              )}
              {carb && (
                <div className="flex items-center justify-between text-amber-400">
                  <span className="flex items-center gap-1">
                    <span>🍚</span>
                    <span>{carb.nombre || carb.name}</span>
                  </span>
                  <span className="text-zinc-400">{carbGrams}g</span>
                </div>
              )}
              {fat && (
                <div className="flex items-center justify-between text-yellow-400">
                  <span className="flex items-center gap-1">
                    <span>🥑</span>
                    <span>{fat.nombre || fat.name}</span>
                  </span>
                  <span className="text-zinc-400">{fatGrams}g</span>
                </div>
              )}
              {fruit && (
                <div className="flex items-center justify-between text-pink-400">
                  <span className="flex items-center gap-1">
                    <span>🍎</span>
                    <span>{fruit.nombre || fruit.name}</span>
                  </span>
                  <span className="text-zinc-400">{fruitGrams}g</span>
                </div>
              )}
              <div className="flex items-center justify-between text-green-400">
                <span className="flex items-center gap-1">
                  <span>🥬</span>
                  <span>Vegetales</span>
                </span>
                <span className="text-zinc-400">100-200g</span>
              </div>
              {!protein && !carb && !fat && !fruit && (
                <span className="text-zinc-500">Sin alimentos definidos</span>
              )}
            </div>
          </div>
        );
      });
    };

    return (
      <div className="min-h-screen bg-[#09090b] py-8 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8 no-print">
            <button 
              onClick={() => navigate('/dashboard')}
              className="inline-flex items-center text-zinc-400 hover:text-pink-600 mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver al Dashboard
            </button>
            
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h1 className="text-3xl font-bold mb-2 text-white">Mi Plan Nutricional Premium</h1>
                <p className="text-zinc-400">Personalizado según tu perfil metabólico</p>
              </div>
              <div className="flex gap-2">
                {savedMenus.length > 0 && (
                  <Button 
                    onClick={() => navigate('/recipes')}
                    className="rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-400 hover:bg-amber-500/30"
                  >
                    ✦ Generador de Recetas
                  </Button>
                )}
                <Button 
                  onClick={() => setShowPlanBuilder(false)}
                  variant="outline"
                  className="rounded-full border-zinc-700 text-zinc-300"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Recalcular
                </Button>
              </div>
            </div>
          </div>

          {/* Resumen de Macros */}
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl p-6 mb-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Scale className="w-6 h-6" />
                <h2 className="text-xl font-bold">Tu Requerimiento Calórico Diario</h2>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-bold ${displayGoal === 'grasa' ? 'bg-orange-500' : 'bg-blue-500'}`}>
                {displayGoal === 'grasa' ? '🔥 Déficit -20%' : '💪 Superávit +15%'}
              </span>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="bg-white/10 rounded-xl p-4 text-center">
                <div className="text-3xl font-bold">{adjustedCalories}</div>
                <div className="text-sm opacity-80">Calorías/día</div>
              </div>
              <div className="bg-white/10 rounded-xl p-4 text-center">
                <div className="text-3xl font-bold">{adjustedProteins}g</div>
                <div className="text-sm opacity-80">Proteínas ({Math.round(distribution.proteinas * 100)}%)</div>
              </div>
              <div className="bg-white/10 rounded-xl p-4 text-center">
                <div className="text-3xl font-bold">{adjustedCarbs}g</div>
                <div className="text-sm opacity-80">Carbos ({Math.round(distribution.carbohidratos * 100)}%)</div>
              </div>
              <div className="bg-white/10 rounded-xl p-4 text-center">
                <div className="text-3xl font-bold">{adjustedFats}g</div>
                <div className="text-sm opacity-80">Grasas ({Math.round(distribution.grasas * 100)}%)</div>
              </div>
            </div>

            {/* Info de TDEE base */}
            <div className="bg-white/5 rounded-lg p-2 text-center text-sm opacity-70">
              Tu TDEE (mantenimiento): {nutritionPlan.tdee || nutritionPlan.calorias_diarias} kcal
            </div>

            {/* Tipo de dieta seleccionada */}
            <div className="bg-white/10 rounded-xl p-4">
              <p className="text-sm opacity-80 mb-2">Distribución de macros:</p>
              <div className="flex items-center gap-2">
                <span className="text-2xl">{DIET_LABELS[currentDiet]?.emoji}</span>
                <span className="font-bold text-lg">{DIET_LABELS[currentDiet]?.label}</span>
              </div>
              <p className="text-sm opacity-80 mt-2">{DIET_EXPLANATIONS[currentDiet]}</p>
            </div>
          </div>

          {/* Grid principal */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Constructor de menú */}
            <div className="lg:col-span-2 no-print">
              <div className="bg-zinc-900 rounded-2xl p-6 border border-zinc-800">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold flex items-center gap-2 text-white">
                    <Utensils className="w-6 h-6 text-pink-500" />
                    Crear Menú
                  </h3>
                  
                  {/* Selector de tipo de menú */}
                  <div className="flex bg-zinc-800 rounded-full p-1">
                    <button
                      onClick={() => setDisplayGoal('grasa')}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                        displayGoal === 'grasa' 
                          ? 'bg-orange-600 text-white' 
                          : 'text-zinc-400 hover:text-white'
                      }`}
                    >
                      🔥 Días bajos en calorías ({grasaMenus}/3)
                    </button>
                    <button
                      onClick={() => setDisplayGoal('masa')}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                        displayGoal === 'masa' 
                          ? 'bg-blue-600 text-white' 
                          : 'text-zinc-400 hover:text-white'
                      }`}
                    >
                      💪 Días altos en calorías ({masaMenus}/3)
                    </button>
                  </div>
                </div>

                {/* Nota explicativa */}
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 mb-4">
                  <p className="text-amber-300 text-sm leading-relaxed">
                    <strong>Dias altos en calorias:</strong> Elegir para el musculo que desees mejorar.
                    <br/>
                    <strong>Dias bajos en calorias:</strong> Elegir para el resto de los dias de entrenamiento.
                    <br/>
                    <strong>Dia de descanso:</strong> Usar dieta de dias bajos en calorias y quitar la comida post entrenamiento.
                  </p>
                </div>

                {/* Info sobre grasa inteligente */}
                <div className="bg-green-900/30 border border-green-500/30 rounded-xl p-3 mb-4">
                  <p className="text-green-400 text-sm flex items-center gap-2">
                    <Info className="w-4 h-4" />
                    Sistema de Grasa Inteligente activado - Se calcula automáticamente si necesitas grasa adicional
                  </p>
                </div>

                {/* Selector de tipo de alimentación */}
                <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-4 mb-4">
                  <p className="text-sm font-medium text-zinc-300 mb-2">Tipo de alimentacion:</p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { value: 'ninguna', label: 'Todas' },
                      { value: 'vegetariano', label: 'Vegetariano' },
                      { value: 'vegano', label: 'Vegano' },
                      { value: 'pescetariano', label: 'Pescetariano' }
                    ].map(opt => (
                      <button key={opt.value} type="button"
                        onClick={() => handleChange('tipo_alimentacion', opt.value)}
                        className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all border ${
                          formData.tipo_alimentacion === opt.value
                            ? 'border-pink-500 bg-pink-500/20 text-pink-300'
                            : 'border-zinc-600 text-zinc-400 hover:border-zinc-400'
                        }`}
                        data-testid={`planbuilder-diet-${opt.value}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Comidas */}
                <div className="space-y-4">
                  {Array.from({ length: nutritionPlan.comidas_por_dia }, (_, mealIndex) => {
                    // Verificar si esta es la comida post-entreno
                    const postEntrenoMeal = nutritionPlan.comida_post_entreno || existingForm?.comida_post_entreno || '2';
                    const isPostEntreno = (mealIndex + 1) === parseInt(postEntrenoMeal);
                    const hasFruit = mealIndex === 0 || isPostEntreno;

                    return (
                      <div key={mealIndex} className={`border rounded-xl p-4 ${isPostEntreno ? 'border-orange-500 bg-orange-500/5' : 'border-zinc-800'}`}>
                        <div className="flex items-center gap-2 mb-3">
                          <span className={`w-8 h-8 ${isPostEntreno ? 'bg-orange-500' : displayGoal === 'masa' ? 'bg-blue-600' : 'bg-orange-600'} rounded-full flex items-center justify-center text-white font-bold text-sm`}>
                            {mealIndex + 1}
                          </span>
                          <div>
                            <h4 className="font-bold text-white">Comida {mealIndex + 1}</h4>
                            {isPostEntreno && (
                              <span className="text-xs text-orange-400 font-medium">🏋️ Post-Entreno</span>
                            )}
                          </div>
                          <span className="text-xs text-zinc-500 ml-auto">
                            P: {proteinPerMeal}g | C: {carbsPerMeal}g | G: {isPostEntreno ? '0' : fatsPerMeal}g
                          </span>
                        </div>
                        
                        {/* Nota para comida post-entreno */}
                        {isPostEntreno && (
                          <div className="bg-orange-900/30 border border-orange-500/30 rounded-lg p-2 mb-3">
                            <p className="text-orange-300 text-xs">
                              ⚡ <strong>Comida Post-Entreno:</strong> Sin grasas adicionales para optimizar la absorción de nutrientes y recuperación muscular.
                            </p>
                          </div>
                        )}

                        <div className="grid grid-cols-2 gap-3">
                          {/* Proteína */}
                          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                            <label className="block text-xs font-bold text-blue-400 mb-1">🥩 Proteína ({proteinPerMeal}g)</label>
                            <select
                              value={selectedFoods[`meal_${mealIndex}_proteina`] || ''}
                              onChange={(e) => handleFoodSelect(mealIndex, 'proteina', e.target.value)}
                              className="w-full p-2 border border-zinc-700 rounded-lg bg-zinc-900 text-white text-sm"
                            >
                              <option value="">Selecciona...</option>
                              {filterFoods(FOOD_DATABASE.proteinas).map(food => {
                                const portion = calculatePortionForSelect(food, proteinPerMeal, 'proteinas');
                                return (
                                  <option key={food.id} value={food.id}>
                                    {food.nombre} - {portion.displayGrams}g ({food.medicion})
                                  </option>
                                );
                              })}
                            </select>
                            {selectedFoods[`meal_${mealIndex}_proteina`] && (
                              <div className="mt-2">
                                <GramEditor
                                  mealIndex={mealIndex}
                                  category="proteina"
                                  grams={mealGrams[`meal_${mealIndex}_proteina`] || 0}
                                  onSet={setMealFoodGramsPremium}
                                  onAdjust={adjustMealFoodGramsPremium}
                                />
                              </div>
                            )}
                          </div>

                          {/* Carbohidrato */}
                          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                            <label className="block text-xs font-bold text-amber-400 mb-1">🍚 Carbohidrato ({hasFruit ? Math.max(carbsPerMeal - 15, 10) : carbsPerMeal}g)</label>
                            <select
                              value={selectedFoods[`meal_${mealIndex}_carbohidrato`] || ''}
                              onChange={(e) => handleFoodSelect(mealIndex, 'carbohidrato', e.target.value)}
                              className="w-full p-2 border border-zinc-700 rounded-lg bg-zinc-900 text-white text-sm"
                            >
                              <option value="">Selecciona...</option>
                              {filterFoods(FOOD_DATABASE.carbohidratos).map(food => {
                                const adjustedCarbs = hasFruit ? Math.max(carbsPerMeal - 15, 10) : carbsPerMeal;
                                const portion = calculatePortionForSelect(food, adjustedCarbs, 'carbohidratos');
                                return (
                                  <option key={food.id} value={food.id}>
                                    {food.nombre} - {portion.displayGrams}g ({food.medicion})
                                  </option>
                                );
                              })}
                            </select>
                            {selectedFoods[`meal_${mealIndex}_carbohidrato`] && (
                              <div className="mt-2">
                                <GramEditor
                                  mealIndex={mealIndex}
                                  category="carbohidrato"
                                  grams={mealGrams[`meal_${mealIndex}_carbohidrato`] || 0}
                                  onSet={setMealFoodGramsPremium}
                                  onAdjust={adjustMealFoodGramsPremium}
                                />
                              </div>
                            )}
                          </div>

                          {/* Grasa Inteligente - Bloqueada si es post-entreno */}
                          {(() => {
                            // Si es comida post-entreno, bloquear grasa
                            if (isPostEntreno) {
                              return (
                                <div className="rounded-lg p-3 bg-orange-900/30 border border-orange-500/50">
                                  <label className="block text-xs font-bold text-orange-400 mb-1">
                                    🥑 Grasa (Bloqueada)
                                  </label>
                                  <div className="flex items-center gap-2 text-orange-300 p-2 bg-orange-900/20 rounded-lg text-xs">
                                    <AlertCircle className="w-4 h-4" />
                                    <span>Sin grasas en comida post-entreno</span>
                                  </div>
                                </div>
                              );
                            }
                            
                            const selectedProteinId = selectedFoods[`meal_${mealIndex}_proteina`];
                            const proteinFood = getFoodById('proteina', selectedProteinId);
                            let smartFatInfo = { needsAdditionalFat: true, remainingFat: fatsPerMeal };
                            
                            if (proteinFood) {
                              const proteinCalc = calculateProteinPortion(proteinFood, proteinPerMeal);
                              smartFatInfo = calculateSmartFat(fatsPerMeal, proteinCalc.fatContributed);
                            }
                            
                            return (
                              <div className={`rounded-lg p-3 ${!smartFatInfo.needsAdditionalFat ? 'bg-green-900/30 border border-green-500/50' : 'bg-green-500/10 border border-green-500/30'}`}>
                                <label className="block text-xs font-bold text-green-400 mb-1">
                                  🥑 Grasa {smartFatInfo.needsAdditionalFat ? `(${Math.round(smartFatInfo.remainingFat)}g faltante)` : '✓'}
                                </label>
                                {!smartFatInfo.needsAdditionalFat ? (
                                  <div className="flex items-center gap-2 text-green-400 p-2 bg-green-900/20 rounded-lg text-xs">
                                    <CheckCircle className="w-4 h-4" />
                                    <span>Sin grasa adicional - la proteína aporta suficiente</span>
                                  </div>
                                ) : (
                                  <>
                                    <select
                                      value={selectedFoods[`meal_${mealIndex}_grasa`] || ''}
                                      onChange={(e) => handleFoodSelect(mealIndex, 'grasa', e.target.value)}
                                      className="w-full p-2 border border-zinc-700 rounded-lg bg-zinc-900 text-white text-sm"
                                    >
                                      <option value="">Selecciona...</option>
                                      {filterFoods(FOOD_DATABASE.grasas).map(food => {
                                        const portion = calculatePortionForSelect(food, smartFatInfo.remainingFat, 'grasas');
                                        return (
                                          <option key={food.id} value={food.id}>
                                            {food.nombre} - {Math.round(portion.grams)}g
                                          </option>
                                        );
                                      })}
                                    </select>
                                    {selectedFoods[`meal_${mealIndex}_grasa`] && (
                                      <div className="mt-2">
                                        <GramEditor
                                          mealIndex={mealIndex}
                                          category="grasa"
                                          grams={mealGrams[`meal_${mealIndex}_grasa`] || 0}
                                          onSet={setMealFoodGramsPremium}
                                          onAdjust={adjustMealFoodGramsPremium}
                                        />
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            );
                          })()}

                          {/* Fruta */}
                          {hasFruit && (
                            <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
                              <label className="block text-xs font-bold text-purple-400 mb-1">🍎 Fruta (~15g carbs)</label>
                              <select
                                value={selectedFoods[`meal_${mealIndex}_fruta`] || ''}
                                onChange={(e) => handleFoodSelect(mealIndex, 'fruta', e.target.value)}
                                className="w-full p-2 border border-zinc-700 rounded-lg bg-zinc-900 text-white text-sm"
                              >
                                <option value="">Selecciona...</option>
                                {filterFoods(FOOD_DATABASE.frutas).map(food => {
                                  const portion = calculatePortionForSelect(food, 15, 'carbohidratos');
                                  return (
                                    <option key={food.id} value={food.id}>
                                      {food.nombre} - {portion.displayGrams}g
                                    </option>
                                  );
                                })}
                              </select>
                              {selectedFoods[`meal_${mealIndex}_fruta`] && (
                                <div className="mt-2">
                                  <GramEditor
                                    mealIndex={mealIndex}
                                    category="fruta"
                                    grams={mealGrams[`meal_${mealIndex}_fruta`] || 0}
                                    colorClass="text-purple-300"
                                    onSet={setMealFoodGramsPremium}
                                    onAdjust={adjustMealFoodGramsPremium}
                                  />
                                </div>
                              )}
                            </div>
                          )}
                          
                          {/* Vegetales - NO en comida post-entreno; opcional (togglable) */}
                          {!isPostEntreno && !hiddenVegetables.has(mealIndex) && (
                          <div className="bg-emerald-900/20 border border-emerald-500/30 rounded-lg p-3 col-span-2">
                            <div className="flex items-center justify-between mb-1">
                              <label className="block text-xs font-bold text-emerald-400">🥦 Vegetales (Libre)</label>
                              <button
                                type="button"
                                onClick={() => setHiddenVegetables(prev => new Set([...prev, mealIndex]))}
                                className="text-xs text-emerald-400 hover:text-red-400 underline"
                                data-testid={`premium-toggle-veg-${mealIndex}`}
                              >
                                Quitar
                              </button>
                            </div>
                            <p className="text-emerald-300 text-xs">Sugerido: 100-200g (no cuenta en macros)</p>
                          </div>
                          )}
                          {!isPostEntreno && hiddenVegetables.has(mealIndex) && (
                            <div className="bg-zinc-800/50 border border-dashed border-zinc-700 rounded-lg p-3 col-span-2 flex items-center justify-between">
                              <span className="text-zinc-500 text-xs">Sin vegetales</span>
                              <button
                                type="button"
                                onClick={() => setHiddenVegetables(prev => {
                                  const s = new Set(prev); s.delete(mealIndex); return s;
                                })}
                                className="text-xs text-emerald-400 hover:text-emerald-300 underline"
                                data-testid={`premium-toggle-veg-${mealIndex}`}
                              >
                                🥦 Agregar
                              </button>
                            </div>
                          )}

                          {/* Total de la comida */}
                          {(() => {
                            const mtot = { cal: 0, p: 0, c: 0, g: 0 };
                            ['proteina', 'carbohidrato', 'grasa', 'fruta'].forEach(cat => {
                              const foodId = selectedFoods[`meal_${mealIndex}_${cat}`];
                              const grams = mealGrams[`meal_${mealIndex}_${cat}`] || 0;
                              if (foodId && grams > 0) {
                                const food = getFoodById(cat, foodId);
                                if (food) {
                                  const mult = grams / 100;
                                  mtot.cal += food.calorias * mult;
                                  mtot.p += food.proteinas * mult;
                                  mtot.c += food.carbohidratos * mult;
                                  mtot.g += food.grasas * mult;
                                }
                              }
                            });
                            if (mtot.cal === 0) return null;
                            return (
                              <div className="col-span-2 pt-2 border-t border-zinc-800 flex justify-end gap-3 text-xs flex-wrap" data-testid={`premium-meal-totals-${mealIndex}`}>
                                <span className="text-zinc-400">Total comida:</span>
                                <span className="text-orange-400 font-medium">{Math.round(mtot.cal)} kcal</span>
                                <span className="text-blue-400">{Math.round(mtot.p)}g P</span>
                                <span className="text-amber-400">{Math.round(mtot.c)}g C</span>
                                <span className="text-green-400">{Math.round(mtot.g)}g G</span>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Total Diario editable */}
                {(() => {
                  const totals = calculateDailyTotalsPremium();
                  const metaKcal = adjustedCalories;
                  const metaP = adjustedProteins;
                  const metaC = adjustedCarbs;
                  const metaG = adjustedFats;
                  const overCal = totals.cal - metaKcal;
                  const isOver = overCal > 50;
                  const hasSelections = totals.cal > 0;
                  if (!hasSelections) return null;
                  return (
                    <div className={`rounded-2xl p-4 mt-4 border ${isOver ? 'bg-red-900/30 border-red-500/40' : 'bg-zinc-900 border-zinc-700'}`} data-testid="premium-daily-totals">
                      <h3 className={`font-bold mb-2 text-sm ${isOver ? 'text-red-300' : 'text-white'}`}>
                        {isOver ? '⚠️ Total Diario (excede la meta)' : '✅ Total Diario'}
                      </h3>
                      <div className="grid grid-cols-4 gap-3 text-center">
                        <div><div className={`text-xl font-bold ${isOver ? 'text-red-400' : 'text-orange-400'}`}>{totals.cal}</div><div className="text-xs text-zinc-400">kcal / {metaKcal}</div></div>
                        <div><div className="text-xl font-bold text-blue-400">{totals.p}g</div><div className="text-xs text-zinc-400">P / {metaP}g</div></div>
                        <div><div className="text-xl font-bold text-amber-400">{totals.c}g</div><div className="text-xs text-zinc-400">C / {metaC}g</div></div>
                        <div><div className="text-xl font-bold text-green-400">{totals.g}g</div><div className="text-xs text-zinc-400">G / {metaG}g</div></div>
                      </div>
                      {isOver && (
                        <p className="text-red-300 text-xs mt-2">
                          El total supera tu meta diaria en {overCal} kcal. Ajusta manualmente los gramos con los editores + / − de cada comida.
                        </p>
                      )}
                    </div>
                  );
                })()}

                {/* Botón Generar Menú */}
                <div className="mt-6">
                  {canCreateMore ? (
                    <Button
                      onClick={generateMenu}
                      className={`w-full ${displayGoal === 'masa' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-orange-600 hover:bg-orange-700'} text-white rounded-full py-6 font-bold text-lg`}
                    >
                      <Plus className="w-5 h-5 mr-2" />
                      {displayGoal === 'masa' ? 'Guardar Menú Días Altos' : 'Guardar Menú Días Bajos'}
                    </Button>
                  ) : (
                    <div className="bg-amber-900/30 border border-amber-500/50 rounded-xl p-6 text-center">
                      <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
                      <p className="text-amber-300 font-bold">Tienes 6 menús guardados. Elimina uno para crear otro.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Columna lateral */}
            <div className="lg:col-span-1 space-y-6">
              {/* Mis Planes Guardados */}
              <div className="bg-zinc-900 rounded-2xl p-6 border border-zinc-800">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2 text-white no-print">
                  <FileText className="w-6 h-6 text-purple-500" />
                  Mis Menús ({savedMenus.length}/{MAX_MENUS})
                </h3>

                {savedMenus.length === 0 ? (
                  <p className="text-zinc-500 text-center py-8 no-print">No has creado menús aún</p>
                ) : (
                  <div className="space-y-3 no-print">
                    {savedMenus.map((menu, idx) => (
                      <div 
                        key={idx} 
                        className={`p-3 rounded-xl cursor-pointer transition-colors ${
                          selectedMenuIndex === idx 
                            ? 'bg-pink-600/20 border-2 border-pink-500' 
                            : 'bg-zinc-800 border border-zinc-700 hover:border-zinc-600'
                        }`}
                        onClick={() => setSelectedMenuIndex(selectedMenuIndex === idx ? null : idx)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span>{menu.menu_type === 'masa' ? '💪' : '🔥'}</span>
                            <span className="font-medium text-white">{menu.menu_type === 'masa' ? 'Día Alto' : 'Día Bajo'} {savedMenus.filter((m, i) => i <= idx && m.menu_type === menu.menu_type).length}</span>
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); deleteMenu(idx); }}
                            className="text-red-500 hover:text-red-400 p-1"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <p className="text-xs text-zinc-500 mt-1">
                          {new Date(menu.created_at).toLocaleDateString('es-ES')}
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                {savedMenus.length > 0 && (
                  <Button
                    onClick={() => window.print()}
                    className="w-full mt-4 bg-pink-600 hover:bg-pink-500 text-white rounded-full py-5 no-print"
                    data-testid="premium-download-all-menus-btn"
                  >
                    📥 Descargar todos los menús en PDF
                  </Button>
                )}

                {/* Vista imprimible de todos los menús guardados (solo visible al imprimir) */}
                <div className="print-only space-y-6 mt-4">
                  {savedMenus.map((menu, idx) => (
                    <div key={idx} className="mb-6">
                      <h4 className="text-lg font-bold mb-2">
                        {menu.menu_type === 'masa' ? '💪' : '🔥'} {menu.menu_type === 'masa' ? 'Día Alto' : 'Día Bajo'} {savedMenus.filter((m, i) => i <= idx && m.menu_type === menu.menu_type).length}
                      </h4>
                      <div className="space-y-2">
                        {renderMenuMeals(menu)}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Vista del menú seleccionado */}
                {selectedMenuIndex !== null && savedMenus[selectedMenuIndex] && (
                  <div className="mt-6 pt-4 border-t border-zinc-700 no-print">
                    <h4 className="text-lg font-bold text-pink-500 mb-4 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      {savedMenus[selectedMenuIndex].name}
                    </h4>
                    <div className="space-y-4 max-h-[400px] overflow-y-auto">
                      {renderMenuMeals(savedMenus[selectedMenuIndex])}
                    </div>
                    <button
                      onClick={() => setSelectedMenuIndex(null)}
                      className="mt-4 w-full text-center text-sm text-zinc-400 hover:text-pink-500"
                    >
                      Cerrar vista
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Formulario inicial
  return (
    <div className="min-h-screen bg-[#09090b] py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button 
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center text-zinc-400 hover:text-pink-600 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al Dashboard
          </button>
          
          <h1 className="text-3xl font-bold mb-2 text-white">Calculador de Macros Premium</h1>
          <p className="text-zinc-400">Personaliza tu plan nutricional según tu perfil metabólico</p>
        </div>

        {/* Nota informativa */}
        <div className="bg-purple-900/30 border border-purple-500/50 rounded-2xl p-6 mb-8">
          <div className="flex items-start gap-3">
            <Scale className="w-6 h-6 text-purple-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-bold text-purple-300 mb-2">PLAN PREMIUM</h3>
              <p className="text-purple-200/80 text-sm leading-relaxed">
                Este plan está diseñado para usuarios avanzados que buscan optimizar composición corporal 
                y conocer nuevos métodos y técnicas para llegar a un nuevo nivel de entrenamiento.
              </p>
              <p className="text-purple-200/80 text-sm leading-relaxed mt-3">
                En nutrición tendrás la opción de elegir entre <strong>3 menús de días altos en calorías</strong> y 
                <strong> 3 menús de días bajos en calorías</strong>, con ajustes de macronutrientes.
              </p>
            </div>
          </div>
        </div>

        {/* Formulario */}
        <form onSubmit={handleSubmit} className="space-y-8">
          
          {/* SECCIÓN 1: DATOS PERSONALES */}
          <div className="bg-zinc-900 rounded-2xl p-6 border border-zinc-800">
            <h2 className="text-xl font-bold mb-6 text-white flex items-center gap-2">
              <span className="w-8 h-8 bg-pink-600 rounded-full flex items-center justify-center text-sm">1</span>
              Datos Personales
            </h2>
            
            <div className="space-y-4">
              {/* Sexo */}
              <div>
                <label className="block text-sm font-medium mb-3 text-zinc-300">Sexo</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer text-zinc-300">
                    <input
                      type="radio"
                      name="sexo"
                      value="mujer"
                      checked={formData.sexo === 'mujer'}
                      onChange={(e) => handleChange('sexo', e.target.value)}
                      className="w-4 h-4 text-pink-600"
                    />
                    <span>Mujer</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer text-zinc-300">
                    <input
                      type="radio"
                      name="sexo"
                      value="hombre"
                      checked={formData.sexo === 'hombre'}
                      onChange={(e) => handleChange('sexo', e.target.value)}
                      className="w-4 h-4 text-pink-600"
                    />
                    <span>Hombre</span>
                  </label>
                </div>
              </div>

              {/* Edad, Peso, Estatura */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-zinc-300">Edad</label>
                  <Input
                    type="number"
                    value={formData.edad}
                    onChange={(e) => handleChange('edad', e.target.value)}
                    placeholder="28"
                    className="rounded-xl bg-zinc-800 border-zinc-700 text-white"
                    required
                    min="15"
                    max="100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-zinc-300">Peso (kg)</label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.peso}
                    onChange={(e) => handleChange('peso', e.target.value)}
                    placeholder="60"
                    className="rounded-xl bg-zinc-800 border-zinc-700 text-white"
                    required
                    min="30"
                    max="300"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-zinc-300">Estatura (cm)</label>
                  <Input
                    type="number"
                    value={formData.estatura}
                    onChange={(e) => handleChange('estatura', e.target.value)}
                    placeholder="165"
                    className="rounded-xl bg-zinc-800 border-zinc-700 text-white"
                    required
                    min="100"
                    max="250"
                  />
                  <p className="text-xs text-zinc-500 mt-1">Sin puntos. Ej: 162</p>
                </div>
              </div>
            </div>
          </div>

          {/* SECCIÓN 2: ACTIVIDAD FÍSICA */}
          <div className="bg-zinc-900 rounded-2xl p-6 border border-zinc-800">
            <h2 className="text-xl font-bold mb-6 text-white flex items-center gap-2">
              <span className="w-8 h-8 bg-pink-600 rounded-full flex items-center justify-center text-sm">2</span>
              Actividad Física
            </h2>
            
            <div>
              <label className="block text-sm font-medium mb-2 text-zinc-300">Nivel de actividad</label>
              <select
                value={formData.nivel_actividad}
                onChange={(e) => handleChange('nivel_actividad', e.target.value)}
                className="w-full px-4 py-3 border border-zinc-700 rounded-xl bg-zinc-800 text-white"
                required
              >
                <option value="sedentario">Sedentario (poco o ningún ejercicio)</option>
                <option value="ligero">Ligero (1-2 días de ejercicio por semana)</option>
                <option value="moderado">Moderado (3-4 días por semana)</option>
                <option value="alto">Alto (5-6 días por semana)</option>
              </select>
            </div>
          </div>

          {/* SECCIÓN 3: OBJETIVO */}
          <div className="bg-zinc-900 rounded-2xl p-6 border border-zinc-800">
            <h2 className="text-xl font-bold mb-6 text-white flex items-center gap-2">
              <span className="w-8 h-8 bg-pink-600 rounded-full flex items-center justify-center text-sm">3</span>
              Objetivo
            </h2>
            
            <div className="grid grid-cols-2 gap-3">
              {[
                { value: 'perder', label: 'Perder grasa', emoji: '🔥' },
                { value: 'ganar', label: 'Ganar masa muscular', emoji: '💪' },
                { value: 'mantener', label: 'Mantener peso', emoji: '⚖️' },
                { value: 'recomposicion', label: 'Recomposición corporal', emoji: '✨' }
              ].map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleChange('objetivo', option.value)}
                  className={`p-4 rounded-xl border-2 transition-all text-left ${
                    formData.objetivo === option.value
                      ? 'border-pink-500 bg-pink-500/20'
                      : 'border-zinc-700 bg-zinc-800 hover:border-zinc-600'
                  }`}
                >
                  <span className="text-2xl mr-2">{option.emoji}</span>
                  <span className={formData.objetivo === option.value ? 'text-white font-medium' : 'text-zinc-300'}>
                    {option.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* SECCIÓN 4: PERFIL METABÓLICO */}
          <div className="bg-zinc-900 rounded-2xl p-6 border border-zinc-800">
            <h2 className="text-xl font-bold mb-2 text-white flex items-center gap-2">
              <span className="w-8 h-8 bg-pink-600 rounded-full flex items-center justify-center text-sm">4</span>
              Perfil Metabólico
            </h2>
            <p className="text-zinc-400 text-sm mb-6">Estas preguntas determinan tu distribución ideal de macronutrientes</p>
            
            <div className="space-y-6">
              {/* Reacción a carbos */}
              <div>
                <label className="block text-sm font-medium mb-3 text-zinc-300">
                  ¿Cómo te sientes después de comer comidas altas en carbohidratos (arroz, pasta, pan)?
                </label>
                <div className="space-y-2">
                  {[
                    { value: 'energia', label: 'Con mucha energía y activa', emoji: '⚡' },
                    { value: 'normal', label: 'Normal, sin cambios notables', emoji: '😊' },
                    { value: 'inflamado', label: 'Inflamada, pesada o con sueño', emoji: '😴' }
                  ].map(option => (
                    <label key={option.value} className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors ${formData.reaccion_carbos === option.value ? 'bg-pink-500/20 border border-pink-500' : 'bg-zinc-800 border border-zinc-700 hover:border-zinc-600'}`}>
                      <input type="radio" name="reaccion_carbos" value={option.value} checked={formData.reaccion_carbos === option.value} onChange={(e) => handleChange('reaccion_carbos', e.target.value)} className="hidden" />
                      <span className="text-xl">{option.emoji}</span>
                      <span className={formData.reaccion_carbos === option.value ? 'text-white' : 'text-zinc-300'}>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Nivel de hambre */}
              <div>
                <label className="block text-sm font-medium mb-3 text-zinc-300">
                  ¿Cómo es tu nivel de hambre durante el día?
                </label>
                <div className="space-y-2">
                  {[
                    { value: 'alto', label: 'Alto — Siempre tengo hambre, como y quedo con ganas', emoji: '🍽️' },
                    { value: 'moderado', label: 'Moderado — Como normal y quedo satisfecha', emoji: '🍴' },
                    { value: 'bajo', label: 'Bajo — A veces se me olvida comer', emoji: '🥄' }
                  ].map(option => (
                    <label key={option.value} className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors ${formData.nivel_hambre === option.value ? 'bg-pink-500/20 border border-pink-500' : 'bg-zinc-800 border border-zinc-700 hover:border-zinc-600'}`}>
                      <input type="radio" name="nivel_hambre" value={option.value} checked={formData.nivel_hambre === option.value} onChange={(e) => handleChange('nivel_hambre', e.target.value)} className="hidden" />
                      <span className="text-xl">{option.emoji}</span>
                      <span className={formData.nivel_hambre === option.value ? 'text-white' : 'text-zinc-300'}>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Tipo de entrenamiento */}
              <div>
                <label className="block text-sm font-medium mb-3 text-zinc-300">
                  ¿Qué tipo de entrenamiento haces principalmente?
                </label>
                <div className="space-y-2">
                  {[
                    { value: 'pesas', label: 'Pesas / Fuerza — Mi enfoque principal es levantar peso', emoji: '🏋️' },
                    { value: 'cardio', label: 'Cardio — Corro, bici, HIIT, baile', emoji: '🏃' },
                    { value: 'mixto', label: 'Mixto — Combino pesas con cardio', emoji: '💪' },
                    { value: 'funcional', label: 'Funcional — CrossFit, calistenia, circuitos', emoji: '🤸' }
                  ].map(option => (
                    <label key={option.value} className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors ${formData.tipo_entrenamiento === option.value ? 'bg-pink-500/20 border border-pink-500' : 'bg-zinc-800 border border-zinc-700 hover:border-zinc-600'}`}>
                      <input type="radio" name="tipo_entrenamiento" value={option.value} checked={formData.tipo_entrenamiento === option.value} onChange={(e) => handleChange('tipo_entrenamiento', e.target.value)} className="hidden" />
                      <span className="text-xl">{option.emoji}</span>
                      <span className={formData.tipo_entrenamiento === option.value ? 'text-white' : 'text-zinc-300'}>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Horario de entrenamiento */}
              <div>
                <label className="block text-sm font-medium mb-3 text-zinc-300">
                  ¿En qué horario entrenas normalmente?
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'manana', label: 'Mañana', emoji: '🌅', sub: '6-10 AM' },
                    { value: 'tarde', label: 'Tarde', emoji: '☀️', sub: '12-5 PM' },
                    { value: 'noche', label: 'Noche', emoji: '🌙', sub: '6-10 PM' }
                  ].map(option => (
                    <button key={option.value} type="button" onClick={() => handleChange('horario_entreno', option.value)}
                      className={`p-3 rounded-xl border-2 transition-all text-center ${formData.horario_entreno === option.value ? 'border-pink-500 bg-pink-500/20' : 'border-zinc-700 bg-zinc-800 hover:border-zinc-600'}`}>
                      <span className="text-xl block">{option.emoji}</span>
                      <span className={`text-sm block mt-1 ${formData.horario_entreno === option.value ? 'text-white font-medium' : 'text-zinc-300'}`}>{option.label}</span>
                      <span className="text-xs text-zinc-500">{option.sub}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Velocidad de metabolismo */}
              <div>
                <label className="block text-sm font-medium mb-3 text-zinc-300">
                  ¿Cómo describirías tu metabolismo?
                </label>
                <div className="space-y-2">
                  {[
                    { value: 'rapido', label: 'Rápido — Puedo comer bastante y no subo de peso fácil', emoji: '🔥' },
                    { value: 'normal', label: 'Normal — Subo y bajo de peso de forma moderada', emoji: '⚖️' },
                    { value: 'lento', label: 'Lento — Me cuesta bajar de peso, subo fácil', emoji: '🐢' }
                  ].map(option => (
                    <label key={option.value} className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors ${formData.velocidad_metabolismo === option.value ? 'bg-pink-500/20 border border-pink-500' : 'bg-zinc-800 border border-zinc-700 hover:border-zinc-600'}`}>
                      <input type="radio" name="velocidad_metabolismo" value={option.value} checked={formData.velocidad_metabolismo === option.value} onChange={(e) => handleChange('velocidad_metabolismo', e.target.value)} className="hidden" />
                      <span className="text-xl">{option.emoji}</span>
                      <span className={formData.velocidad_metabolismo === option.value ? 'text-white' : 'text-zinc-300'}>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Retención de líquidos */}
              <div>
                <label className="block text-sm font-medium mb-3 text-zinc-300">
                  ¿Sueles sentirte hinchada o con retención de líquidos?
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: 'si', label: 'Sí, con frecuencia', emoji: '💧' },
                    { value: 'no', label: 'No, casi nunca', emoji: '✅' }
                  ].map(option => (
                    <button key={option.value} type="button" onClick={() => handleChange('retencion_liquidos', option.value)}
                      className={`p-3 rounded-xl border-2 transition-all flex items-center gap-2 justify-center ${formData.retencion_liquidos === option.value ? 'border-pink-500 bg-pink-500/20' : 'border-zinc-700 bg-zinc-800 hover:border-zinc-600'}`}>
                      <span className="text-xl">{option.emoji}</span>
                      <span className={formData.retencion_liquidos === option.value ? 'text-white font-medium' : 'text-zinc-300'}>{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Calidad de sueño */}
              <div>
                <label className="block text-sm font-medium mb-3 text-zinc-300">
                  ¿Cómo es tu calidad de sueño?
                </label>
                <div className="space-y-2">
                  {[
                    { value: 'buena', label: 'Buena — Duermo 7-8 horas y descanso bien', emoji: '😴' },
                    { value: 'regular', label: 'Regular — A veces me cuesta dormir o descansar', emoji: '😐' },
                    { value: 'mala', label: 'Mala — Duermo poco o mal, me levanto cansada', emoji: '😩' }
                  ].map(option => (
                    <label key={option.value} className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors ${formData.calidad_sueno === option.value ? 'bg-pink-500/20 border border-pink-500' : 'bg-zinc-800 border border-zinc-700 hover:border-zinc-600'}`}>
                      <input type="radio" name="calidad_sueno" value={option.value} checked={formData.calidad_sueno === option.value} onChange={(e) => handleChange('calidad_sueno', e.target.value)} className="hidden" />
                      <span className="text-xl">{option.emoji}</span>
                      <span className={formData.calidad_sueno === option.value ? 'text-white' : 'text-zinc-300'}>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* DIAGNÓSTICO PERSONALIZADO */}
          {formData.edad && formData.peso && formData.estatura && (() => {
            const diagnostic = getDiagnostic();
            const dietInfo = DIET_LABELS[diagnostic.recommended];
            return (
              <div className="bg-gradient-to-br from-purple-900/40 to-pink-900/40 rounded-2xl p-6 border border-purple-500/30" data-testid="diagnostic-card">
                <div className="flex items-center gap-2 mb-4">
                  <span className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-sm text-white font-bold">✦</span>
                  <h2 className="text-xl font-bold text-white">Tu Diagnóstico Personalizado</h2>
                </div>
                
                {/* Tipo metabólico */}
                <div className="bg-black/30 rounded-xl p-4 mb-4 border border-purple-500/20">
                  <p className="text-purple-300 text-xs font-bold uppercase tracking-wider mb-1">Tu tipo metabólico</p>
                  <h3 className="text-white text-lg font-bold mb-2">{diagnostic.metabolicType}</h3>
                  <p className="text-zinc-400 text-sm leading-relaxed">{diagnostic.metabolicDescription}</p>
                </div>

                {/* Dieta recomendada */}
                <div className="bg-black/30 rounded-xl p-4 mb-4 border border-pink-500/20">
                  <p className="text-pink-300 text-xs font-bold uppercase tracking-wider mb-1">Dieta ideal para tu caso</p>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-3xl">{dietInfo?.emoji}</span>
                    <h3 className="text-white text-lg font-bold">{dietInfo?.label}</h3>
                  </div>
                  <p className="text-zinc-400 text-sm leading-relaxed">{DIET_EXPLANATIONS[diagnostic.recommended]}</p>
                  <div className="mt-3 flex gap-4 text-xs">
                    <span className="text-red-400 font-bold">Proteínas: {DIET_DISTRIBUTIONS[diagnostic.recommended].proteinas * 100}%</span>
                    <span className="text-amber-400 font-bold">Carbos: {DIET_DISTRIBUTIONS[diagnostic.recommended].carbohidratos * 100}%</span>
                    <span className="text-yellow-400 font-bold">Grasas: {DIET_DISTRIBUTIONS[diagnostic.recommended].grasas * 100}%</span>
                  </div>
                </div>

                {/* Consejos personalizados */}
                {diagnostic.tips.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-zinc-300 text-xs font-bold uppercase tracking-wider">Consejos para ti</p>
                    {diagnostic.tips.map((tip, idx) => (
                      <div key={idx} className="flex items-start gap-2 bg-black/20 rounded-lg p-3 border border-zinc-700/50">
                        <span className="text-amber-400 text-sm mt-0.5">💡</span>
                        <p className="text-zinc-400 text-sm">{tip}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          {/* SECCIÓN 5: PREFERENCIA DE DIETA */}
          <div className="bg-zinc-900 rounded-2xl p-6 border border-zinc-800">
            <h2 className="text-xl font-bold mb-2 text-white flex items-center gap-2">
              <span className="w-8 h-8 bg-pink-600 rounded-full flex items-center justify-center text-sm">5</span>
              Preferencia de Dieta
            </h2>
            <p className="text-zinc-400 text-sm mb-6">
              Según tu perfil hemos pre-seleccionado la opción ideal. Puedes cambiarla si prefieres otra distribución.
            </p>
            
            <div className="space-y-3">
              {Object.entries(DIET_LABELS).map(([key, { emoji, label }]) => {
                const isRecommended = getRecommendedDiet() === key;
                return (
                  <div key={key}>
                    <button
                      type="button"
                      onClick={() => handleChange('preferencia_dieta', key)}
                      className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
                        formData.preferencia_dieta === key
                          ? 'border-pink-500 bg-pink-500/20'
                          : 'border-zinc-700 bg-zinc-800 hover:border-zinc-600'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{emoji}</span>
                        <span className={formData.preferencia_dieta === key ? 'text-white font-medium' : 'text-zinc-300'}>
                          {label}
                        </span>
                        {isRecommended && (
                          <span className="bg-purple-500/30 text-purple-300 text-[10px] font-bold px-2 py-0.5 rounded-full border border-purple-500/30 uppercase tracking-wider">
                            Recomendada
                          </span>
                        )}
                      </div>
                      {formData.preferencia_dieta === key && (
                        <CheckCircle className="w-5 h-5 text-pink-500" />
                      )}
                    </button>
                    
                    {formData.preferencia_dieta === key && (
                      <div className="mt-2 p-4 bg-zinc-800 rounded-xl border border-zinc-700">
                        <p className="text-zinc-300 text-sm">{DIET_EXPLANATIONS[key]}</p>
                        <div className="mt-3 flex gap-4 text-xs text-zinc-400">
                          <span>Proteínas: {DIET_DISTRIBUTIONS[key].proteinas * 100}%</span>
                          <span>Carbos: {DIET_DISTRIBUTIONS[key].carbohidratos * 100}%</span>
                          <span>Grasas: {DIET_DISTRIBUTIONS[key].grasas * 100}%</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* SECCIÓN 6: PREFERENCIAS ADICIONALES */}
          <div className="bg-zinc-900 rounded-2xl p-6 border border-zinc-800">
            <h2 className="text-xl font-bold mb-6 text-white flex items-center gap-2">
              <span className="w-8 h-8 bg-pink-600 rounded-full flex items-center justify-center text-sm">6</span>
              Distribución de Comidas
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-zinc-300">Número de comidas al día</label>
                <select
                  value={formData.numero_comidas}
                  onChange={(e) => {
                    handleChange('numero_comidas', e.target.value);
                    // Si la comida post entreno es mayor al nuevo número, ajustar
                    if (parseInt(formData.comida_post_entreno) > parseInt(e.target.value)) {
                      handleChange('comida_post_entreno', '2');
                    }
                  }}
                  className="w-full px-4 py-3 border border-zinc-700 rounded-xl bg-zinc-800 text-white"
                  required
                >
                  <option value="3">3 comidas</option>
                  <option value="4">4 comidas</option>
                  <option value="5">5 comidas</option>
                  <option value="6">6 comidas</option>
                </select>
              </div>

              {/* Selector de comida post entreno */}
              <div>
                <label className="block text-sm font-medium mb-2 text-zinc-300">
                  ¿Cuál será tu comida post-entreno? 🏋️
                </label>
                <select
                  value={formData.comida_post_entreno}
                  onChange={(e) => handleChange('comida_post_entreno', e.target.value)}
                  className="w-full px-4 py-3 border border-zinc-700 rounded-xl bg-zinc-800 text-white"
                >
                  {Array.from({ length: parseInt(formData.numero_comidas) }, (_, i) => {
                    const mealNames = ['Comida 1', 'Comida 2', 'Comida 3', 'Comida 4', 'Comida 5', 'Comida 6'];
                    return (
                      <option key={i + 1} value={String(i + 1)}>
                        {mealNames[i] || `Comida ${i + 1}`}
                      </option>
                    );
                  })}
                </select>
                <p className="text-xs text-zinc-500 mt-1">
                  Esta comida NO tendrá grasas adicionales para optimizar la recuperación muscular.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-zinc-300">
                  ¿Sigues algun tipo de alimentacion especial?
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: 'ninguna', label: 'Ninguna', desc: 'Todos los alimentos' },
                    { value: 'vegetariano', label: 'Vegetariano', desc: 'Sin carne ni pescado' },
                    { value: 'vegano', label: 'Vegano estricto', desc: 'Solo alimentos vegetales' },
                    { value: 'pescetariano', label: 'Pescetariano', desc: 'Sin carne roja ni pollo' }
                  ].map(opt => (
                    <button key={opt.value} type="button"
                      onClick={() => handleChange('tipo_alimentacion', opt.value)}
                      className={`p-3 rounded-xl border text-left transition-all ${
                        formData.tipo_alimentacion === opt.value
                          ? 'border-pink-500 bg-pink-500/10 text-white'
                          : 'border-zinc-700 bg-zinc-800/50 text-zinc-400 hover:border-zinc-500'
                      }`}
                      data-testid={`diet-type-${opt.value}`}
                    >
                      <p className="font-bold text-sm">{opt.label}</p>
                      <p className="text-xs opacity-70">{opt.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-zinc-300">Alergias (opcional)</label>
                <textarea
                  value={formData.alergias}
                  onChange={(e) => handleChange('alergias', e.target.value)}
                  placeholder="Ej: maní, mariscos, gluten"
                  rows={2}
                  className="w-full px-4 py-2 border border-zinc-700 rounded-xl resize-none bg-zinc-800 text-white placeholder-zinc-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-zinc-300">Alimentos que NO te gustan (opcional)</label>
                <textarea
                  value={formData.alimentos_no_gustan}
                  onChange={(e) => handleChange('alimentos_no_gustan', e.target.value)}
                  placeholder="Ej: hígado, brócoli, pescado"
                  rows={2}
                  className="w-full px-4 py-2 border border-zinc-700 rounded-xl resize-none bg-zinc-800 text-white placeholder-zinc-500"
                />
              </div>
            </div>
          </div>

          {/* Botón Calcular */}
          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white rounded-full py-6 font-bold text-lg"
          >
            {submitting ? (
              'Calculando...'
            ) : (
              <>
                <Calculator className="w-5 h-5 mr-2" />
                Calcular Mi Plan Personalizado
              </>
            )}
          </Button>

          <p className="text-xs text-zinc-500 text-center">
            Según tu perfil metabólico, el sistema determinará la distribución de macronutrientes más adecuada para ti.
          </p>
        </form>
      </div>
    </div>
  );
};

export default NutritionFormPremium;
